/**
 * BookedTabContent Component
 * Displays paid rituals the user has booked/joined.
 * Shows booking information with expert details and session times.
 */

import { format } from "date-fns"
import { AlertCircle, Banknote, Calendar, Link2, MapPin, User } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { RefundRequestModal } from "@/components/modals/RefundRequestModal"
import { Avatar } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  getRefundRequestStatus,
  submitRefundRequest,
} from "@/features/booking/services/booking.service"
import { useStartExpertGroupChat } from "@/features/chats/hooks/useRitualGroupChat"
import { CoachingBookSessionModal } from "@/features/coaching/components/CoachingBookSessionModal"
import { addCoachingToCalendar } from "@/features/coaching/services/coaching.service"
import { useUserBookedSessions } from "@/features/profile/hooks/useUserBookedSessions"
import type { BookingItem } from "@/features/profile/services/user-rituals.service"
import { useDebounce } from "@/hooks/useDebounce"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"

interface BookedTabContentProps {
  searchQuery?: string
}

interface RefundStatus {
  hasRequest: boolean
  status?: "PENDING" | "APPROVED" | "REJECTED" | "REFUNDED"
}

export function BookedTabContent({ searchQuery }: BookedTabContentProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const debouncedSearch = useDebounce(searchQuery || "", 500)
  const startExpertGroupChat = useStartExpertGroupChat()
  const [addingToCalendarBookingIds, setAddingToCalendarBookingIds] = useState<Set<string>>(
    new Set(),
  )
  const [manageBooking, setManageBooking] = useState<BookingItem | null>(null)
  const [refundStatuses, setRefundStatuses] = useState<Map<string, RefundStatus>>(new Map())
  const [refundModal, setRefundModal] = useState<{
    isOpen: boolean
    booking: BookingItem | null
  }>({ isOpen: false, booking: null })
  const fetchedRefundsRef = useRef<Set<string>>(new Set())

  const { data, isLoading, isError } = useUserBookedSessions({
    limit: 50,
    search: debouncedSearch || undefined,
  })

  const handleChatWithExpert = (bookingId: string, ritualId?: string) => {
    if (startExpertGroupChat.isPending) return
    startExpertGroupChat.mutate(
      { bookingId, ritualId },
      {
        onSuccess: conversation => {
          navigate(`/chats/ritual/${conversation.id}`)
        },
        onError: () => {
          toast.error("Couldn't open chat with expert. Please try again.")
        },
      },
    )
  }

  const addToGoogleCalendar = async (bookingId: string) => {
    if (addingToCalendarBookingIds.has(bookingId)) return

    setAddingToCalendarBookingIds(prev => new Set(prev).add(bookingId))
    try {
      const result = await addCoachingToCalendar(bookingId)
      toast.success("Added to Google Calendar")
      // Open the Google Calendar event in a new tab
      window.open(result.htmlLink, "_blank")
    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number
          data?: { data?: { code?: string }; message?: string }
        }
      }
      const errorMessage = axiosError.response?.data?.message || "Failed to add to Google Calendar"

      if (errorMessage === "Google account not connected") {
        toast.error(errorMessage)
        // Automatically redirect to settings connected-accounts section
        setTimeout(() => {
          window.location.href = "/settings?section=connected-accounts"
        }, 1500)
      } else if (axiosError.response?.status === 409) {
        toast.info("This booking is already in your Google Calendar")
      } else {
        toast.error(errorMessage)
      }
    } finally {
      setAddingToCalendarBookingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(bookingId)
        return newSet
      })
    }
  }

  const fetchRefundStatus = useCallback(async (ritualId: string) => {
    if (fetchedRefundsRef.current.has(ritualId)) return
    fetchedRefundsRef.current.add(ritualId)

    try {
      const status = await getRefundRequestStatus(ritualId)
      setRefundStatuses(prev => new Map(prev).set(ritualId, status))
    } catch {
      fetchedRefundsRef.current.delete(ritualId)
    }
  }, [])

  useEffect(() => {
    if (!data?.bookings) return
    const canceledRituals = data.bookings
      .filter(b => b.ritual?.canceledAt && b.ritual.isPaid)
      .map(b => b.ritual?.id)
      .filter((id): id is string => id !== undefined)

    canceledRituals.forEach(ritualId => {
      fetchRefundStatus(ritualId)
    })
  }, [data, fetchRefundStatus])

  const handleRequestRefund = async () => {
    const ritualId = refundModal.booking?.ritual?.id
    if (!ritualId) return
    await submitRefundRequest(ritualId)
    setRefundStatuses(prev =>
      new Map(prev).set(ritualId, {
        hasRequest: true,
        status: "PENDING",
      }),
    )
  }

  const getRefundStatusBadge = (status?: string) => {
    switch (status) {
      case "PENDING":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-400 text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Refund Pending
          </span>
        )
      case "APPROVED":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
            Refund Approved
          </span>
        )
      case "REJECTED":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Refund Rejected
          </span>
        )
      case "REFUNDED":
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
            Refunded
          </span>
        )
      default:
        return null
    }
  }

  const bookings = data?.bookings ?? []
  const getBookingTitle = (booking: BookingItem) =>
    booking.ritual?.title || booking.coachingSession?.title || "Session"
  const getBookingLocation = (booking: BookingItem) =>
    booking.ritual?.location || booking.coachingSession?.location || null
  const getBookingUrl = (booking: BookingItem) =>
    booking.ritual?.websiteLink ||
    booking.coachingSession?.meetingLink ||
    booking.coachingSession?.websiteLink ||
    booking.expert?.websiteUrl ||
    null
  const getBookingCoverImage = (booking: BookingItem) => {
    const url = booking.ritual?.coverImage || booking.coachingSession?.coverImage
    if (!url) return null
    return url.startsWith("http") ? url : getAssetUrl(url)
  }
  const getBookingPrice = (booking: BookingItem) => {
    if (booking.ritual) {
      return {
        label: booking.ritual.isPaid
          ? `${booking.ritual.feeCurrency || "$"}${booking.ritual.fee || 0}`
          : "Free",
        isPaid: booking.ritual.isPaid,
      }
    }

    if (booking.coachingSession) {
      const isPaid = booking.coachingSession.participationFee === "Paid"
      return {
        label: isPaid
          ? `${booking.coachingSession.feeCurrency || "$"}${booking.coachingSession.price || 0}`
          : "Free",
        isPaid,
      }
    }

    return { label: "Free", isPaid: false }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-primary/50">{t("sessionsSubTabs.loading")}</p>
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-red-400">{t("sessionsSubTabs.error")}</p>
      </div>
    )
  }

  if (bookings.length === 0) {
    return (
      <div className="pt-8">
        <p className="text-sm text-primary opacity-90 leading-relaxed">
          {t("sessionsSubTabs.emptyBookedDescription")}
        </p>
      </div>
    )
  }

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":")
    const date = new Date()
    date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0)
    return format(date, "h:mm a")
  }

  const calculateDuration = (startTime: string, endTime: string) => {
    const [startHours, startMins] = startTime.split(":").map(Number)
    const [endHours, endMins] = endTime.split(":").map(Number)
    const totalStartMins = startHours * 60 + startMins
    const totalEndMins = endHours * 60 + endMins
    const duration = totalEndMins - totalStartMins
    return `${duration} Min`
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3.75 md:gap-5 mt-4">
        {bookings.map((booking: BookingItem) => {
          const bookingLocation = getBookingLocation(booking)
          const bookingUrl = getBookingUrl(booking)
          const bookingPrice = getBookingPrice(booking)
          const bookingCoverImage = getBookingCoverImage(booking)

          return (
            <div
              key={booking.id}
              className="bg-[#FFFFFF0D] border border-[#FFFFFF1A] rounded-br-[40px] transition-colors  rounded-tl-[30px] relative overflow-hidden"
            >
              {bookingCoverImage && (
                <div className="aspect-[16/9] rounded-tl-[30px] w-full overflow-hidden bg-white/5">
                  <img
                    src={bookingCoverImage}
                    alt={getBookingTitle(booking)}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              <div className="p-5">
                {/* Ritual/Session Title */}
                <h3 className="text-sm md:text-[18px] font-semibold leading-[22px] text-[#F2F0EB] mb-3.75">
                  {getBookingTitle(booking)}
                </h3>

                {/* Expert info with avatar */}
                <div className="flex items-center gap-2.5 mb-3.75">
                  <Avatar
                    src={booking.expert?.profilePictureUrl || undefined}
                    alt={booking.expert?.name || "Expert"}
                    fallback={booking.expert?.name || "?"}
                    size="md"
                    className="rounded-[5px] w-7.5 h-7.5"
                  />
                  <div className="flex gap-2.5 items-center">
                    <h4 className="text-[14px] font-semibold leading-[22px] text-[#F2F0EB]">
                      {booking.expert?.name || "Expert"}
                    </h4>
                    <p className="text-[14px] font-normal leading-[22px] text-[#F2F0EB80]">
                      {booking.expert?.followersCount ?? 0} followers
                    </p>
                  </div>
                </div>

                {/* Message */}
                {booking.message && (
                  <div className="mb-3.75">
                    <p className="text-[13px] font-normal leading-[25px] text-[#F2F0EB]">
                      {booking.message}
                    </p>
                  </div>
                )}

                {/* Details with icons */}
                <div className="space-y-2.5 md:space-y-2 mb-5">
                  {/* Date and Time */}
                  <div className="flex items-center gap-2.5">
                    <Calendar className="w-6 h-6 text-white opacity-70" />
                    <p className="text-[14px] font-normal leading-[24px] text-white underline">
                      {booking.date ? format(new Date(booking.date), "EEEE, MMMM d, yyyy") : "TBD"}
                      {booking.startTime && booking.endTime
                        ? ` at ${formatTime(booking.startTime)} – ${calculateDuration(booking.startTime, booking.endTime)}`
                        : ""}
                    </p>
                  </div>

                  {/* Location */}
                  {bookingLocation && (
                    <div className="flex items-center gap-2.5">
                      <MapPin className="w-6 h-6 text-white opacity-70" />
                      <p className="text-[14px] font-normal leading-[24px] text-white">
                        {bookingLocation}
                      </p>
                    </div>
                  )}

                  {/* Price */}
                  {bookingPrice.isPaid ? (
                    <div className="flex items-center gap-2.5">
                      <Banknote className="w-6 h-6 text-brand-terracotta opacity-70" />
                      <p className="text-[14px] font-normal leading-[24px] text-brand-olive">
                        <span className="font-semibold">{bookingPrice.label}</span>{" "}
                      </p>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2.5">
                      <Banknote className="w-6 h-6 text-brand-olive opacity-70" />
                      <p className="text-[14px] font-normal leading-[24px] text-brand-olive">
                        <span className="font-semibold">{bookingPrice.label}</span>
                      </p>
                    </div>
                  )}
                  <div className="flex items-center gap-2.5">
                    <User className="w-6 h-6 text-white opacity-70" />
                    <p className="text-[14px] font-normal leading-[24px] text-white">
                      Private session with{" "}
                      <span className="font-semibold">{booking.expert?.name || "Expert"}</span>
                    </p>
                  </div>

                  {/* Website */}
                  {bookingUrl && (
                    <div className="flex items-center gap-2.5">
                      <Link2 className="w-6 h-6 text-white opacity-70" />
                      <a
                        href={bookingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[14px] font-normal leading-[24px] text-white underline hover:opacity-80 transition-opacity"
                      >
                        {bookingUrl}
                      </a>
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col md:flex-row flex-wrap gap-3.75 md:gap-3 w-full">
                  {booking.coachingSession && (
                    <Button
                      variant="outline"
                      onClick={() => setManageBooking(booking)}
                      className="min-h-[50px] rounded-full border border-white text-white text-[14px] font-semibold uppercase hover:bg-white/10 transition-colors flex-1 min-w-fit"
                    >
                      Manage
                    </Button>
                  )}

                  {/* Add to Calendar Button - Only for coaching sessions (ritual is null) */}
                  {booking.ritual === null && (
                    <Button
                      variant="outline"
                      onClick={() => addToGoogleCalendar(booking.id)}
                      disabled={addingToCalendarBookingIds.has(booking.id)}
                      className="min-h-[50px] rounded-full border border-white text-white text-[14px] font-semibold uppercase flex items-center justify-center gap-2.5 hover:bg-white/10 transition-colors disabled:opacity-50 flex-1 min-w-fit"
                    >
                      <Calendar className="w-[25px] h-[25px]" />
                      <span className="hidden sm:inline">
                        {addingToCalendarBookingIds.has(booking.id)
                          ? "Adding..."
                          : "Add to Calendar"}
                      </span>
                      <span className="sm:hidden">
                        {addingToCalendarBookingIds.has(booking.id) ? "..." : "Calendar"}
                      </span>
                    </Button>
                  )}

                  {/* Refund Button/Status - Only for canceled paid rituals */}
                  {booking.ritual?.canceledAt &&
                    booking.ritual.isPaid &&
                    (() => {
                      const ritualId = booking.ritual?.id
                      if (!ritualId) return null
                      const refundStatus = refundStatuses.get(ritualId)
                      if (refundStatus?.hasRequest) {
                        return getRefundStatusBadge(refundStatus.status)
                      }
                      return (
                        <Button
                          variant="outline"
                          onClick={() => setRefundModal({ isOpen: true, booking })}
                          className="min-h-[50px] rounded-full border border-[#FF6B35] text-[#FF6B35] text-[14px] font-semibold uppercase hover:bg-[#FF6B35]/10 transition-colors flex-1 min-w-fit"
                        >
                          Request Refund
                        </Button>
                      )
                    })()}

                  {/* Chat with Expert Button */}
                  <Button
                    variant="primary"
                    onClick={() => handleChatWithExpert(booking.id, booking.ritual?.id)}
                    disabled={startExpertGroupChat.isPending}
                    className="min-h-[50px] rounded-full text-[14px] font-semibold uppercase hover:opacity-90 transition-colors disabled:opacity-50 flex-1 min-w-fit"
                  >
                    Chat with Expert
                  </Button>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {manageBooking?.coachingSession && (
        <CoachingBookSessionModal
          open={!!manageBooking}
          onOpenChange={open => !open && setManageBooking(null)}
          bookingId={manageBooking.id}
          session={{
            id: manageBooking.coachingSession.id,
            coverImage: manageBooking.coachingSession.coverImage ?? null,
            title: manageBooking.coachingSession.title,
            format: ["IN_PERSON", "VIRTUAL", "HYBRID"].includes(
              manageBooking.coachingSession.format,
            )
              ? (manageBooking.coachingSession.format as "IN_PERSON" | "VIRTUAL" | "HYBRID")
              : "VIRTUAL",
            duration: manageBooking.coachingSession.duration,
            durationType: "minutes" as const,
            price: manageBooking.coachingSession.price,
            participationFee: manageBooking.coachingSession.participationFee,
            websiteLink: manageBooking.coachingSession.websiteLink ?? null,
            streetAddress: manageBooking.coachingSession.location ?? null,
            tags: [],
          }}
          expert={{
            id: manageBooking.expert?.id ?? "",
            name: manageBooking.expert?.name ?? null,
            username: manageBooking.expert?.username ?? null,
            profilePictureUrl: manageBooking.expert?.profilePictureUrl ?? null,
          }}
        />
      )}

      {refundModal.booking?.ritual && (
        <RefundRequestModal
          isOpen={refundModal.isOpen}
          onOpenChange={open =>
            setRefundModal({ isOpen: open, booking: open ? refundModal.booking : null })
          }
          ritualTitle={refundModal.booking.ritual.title}
          amount={`${refundModal.booking.ritual.feeCurrency || "$"}${refundModal.booking.ritual.fee || 0}`}
          onConfirm={handleRequestRefund}
        />
      )}
    </>
  )
}
